import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Check,
  Upload,
  Wand2,
  MailCheck,
} from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";
import { useCheckout } from "@/lib/checkout";

const H2 = ({ children }) => (
  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white mt-16 mb-6">
    {children}
  </h2>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3 text-[15px] text-zinc-200 leading-relaxed">
    <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30 mt-0.5">
      <Check className="h-3 w-3 text-emerald-400" />
    </span>
    <span>{children}</span>
  </li>
);

const faqs = [
  {
    q: "Is an AI resume builder good for ATS?",
    a: "Only if it's designed for it. CV AI PRO's engine is built specifically around ATS requirements — clean structure, keyword optimization, no complex tables or hidden formatting. Every resume is human-reviewed before delivery to guarantee compatibility.",
  },
  {
    q: "How is this different from free resume builders?",
    a: "Free builders give you a blank template and make you fill it in. We deliver a finished, professionally rewritten and redesigned PDF from the CV you already have — no writing, editing, or design work required on your end.",
  },
  {
    q: "What if I don't have a resume yet?",
    a: "No problem. Use our quick form to share your work experience, skills, and target role. Our AI will draft your resume from scratch in minutes.",
  },
];

export default function AiResumeBuilder() {
  const { startCheckout } = useCheckout();
  usePageMeta({
    title: "AI Resume Builder | ATS-Optimized Resume Generator",
    description:
      "The AI resume builder that does the work for you. Generate a professional, ATS-friendly resume in minutes with CV AI PRO — no DIY templates, just a finished PDF.",
    path: "/ai-resume-builder",
  });

  return (
    <div
      className="relative min-h-screen bg-black text-white"
      data-testid="arb-page"
    >
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-indigo-600/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute top-40 -right-24 h-[420px] w-[420px] rounded-full bg-purple-600/15 blur-[140px]"
      />
      <div aria-hidden className="absolute inset-0 grid-bg opacity-60" />

      <header className="relative">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="arb-logo-link">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-white">
              CV AI <span className="text-zinc-400 font-medium">PRO</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="arb-back-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to home
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">
            AI Resume Builder
          </p>
          <h1
            className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-white text-balance"
            data-testid="arb-h1"
          >
            AI Resume Builder That Does The Work For You
          </h1>
          <p
            className="mt-6 text-zinc-400 leading-relaxed text-balance text-[17px]"
            data-testid="arb-intro"
          >
            Most AI resume builders make you do the work. CV AI PRO generates a
            finished, professionally designed, ATS-ready resume from the CV you
            already have — delivered in minutes.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => startCheckout("cv_pro")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-7 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
              data-testid="arb-cta-top"
            >
              Build My Resume
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <Link
              to="/resume-writing-service"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              data-testid="arb-link-service"
            >
              See our writing service
            </Link>
          </div>
        </motion.div>

        <H2>Why our AI resume builder is different</H2>
        <ul className="space-y-3">
          <Bullet>Delivers a finished PDF, not a template</Bullet>
          <Bullet>
            Trained on what recruiters scan in the first 6 seconds
          </Bullet>
          <Bullet>
            Built to pass the ATS systems used by 99% of Fortune 500 companies
          </Bullet>
          <Bullet>Human-reviewed before delivery</Bullet>
        </ul>

        <H2>How it works</H2>
        <ol className="space-y-4">
          {[
            { Icon: Upload, text: "Upload your old CV or share your information" },
            { Icon: Wand2, text: "AI creates and optimizes your resume" },
            {
              Icon: MailCheck,
              text: "Receive your professional CV ready to apply",
            },
          ].map(({ Icon, text }, i) => (
            <li
              key={i}
              className="flex items-start gap-4 rounded-2xl border border-white/10 bg-zinc-950/60 p-5"
              data-testid={`arb-step-${i}`}
            >
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-zinc-900 to-black">
                <Icon className="h-4 w-4 text-white/90" />
              </span>
              <div>
                <p className="text-xs text-zinc-500 font-mono">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-1 text-[15px] text-zinc-200">{text}</p>
              </div>
            </li>
          ))}
        </ol>

        <H2>FAQ</H2>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div
              key={i}
              className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5"
              data-testid={`arb-faq-${i}`}
            >
              <p className="text-[15px] text-white font-medium">{f.q}</p>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                {f.a}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col items-center gap-3 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-8 text-center">
          <p className="text-lg font-medium text-white">
            Skip the template. Get the resume.
          </p>
          <button
            onClick={() => startCheckout("cv_pro")}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-7 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
            data-testid="arb-cta-bottom"
          >
            Build My Resume
            <ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-3 text-xs text-zinc-500">
            Or see the full{" "}
            <Link to="/" className="text-zinc-300 hover:text-white underline underline-offset-2">
              homepage
            </Link>{" "}
            and{" "}
            <Link
              to="/resume-writing-service"
              className="text-zinc-300 hover:text-white underline underline-offset-2"
            >
              done-for-you writing service
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
